import { useContext } from 'react';
import AuthContext from '../../contexts/authContext';

/**
 * Custom hook for permission management
 * Provides easy-to-use permission checking functions
 */
export const usePermissions = () => {
	const { userPermissions, isLoading } = useContext(AuthContext);

	/**
	 * Check if user has a specific permission
	 * @param permission - Permission name to check
	 * @returns boolean
	 */
	const hasPermission = (permission: string): boolean => {
		if (isLoading) return false;
		return userPermissions.includes(permission);
	};

	const hasHierarchicalPermission = (permission: string, parentPermission?: string): boolean => {
		if (isLoading) return false;
		
		if (!hasPermission(permission)) return false;
		
		if (parentPermission && !hasPermission(parentPermission)) {
			return false;
		}
		
		return true;
	};

	/**
	 * Check if user has any of the specified permissions
	 * @param permissions - Array of permission names
	 * @returns boolean
	 */
	const hasAnyPermission = (permissions: string[]): boolean => {
		if (isLoading) return false;
		return permissions.some(permission => userPermissions.includes(permission));
	};

	/**
	 * Check if user has all of the specified permissions
	 * @param permissions - Array of permission names
	 * @returns boolean
	 */
	const hasAllPermissions = (permissions: string[]): boolean => {
		if (isLoading) return false;
		return permissions.every(permission => userPermissions.includes(permission));
	};

	/**
	 * Check if user has permission level (read, write, delete, admin)
	 * @param resource - Resource name (e.g., 'user', 'customer', 'transaction')
	 * @param level - Permission level ('view', 'create', 'update', 'delete', 'manage')
	 * @returns boolean
	 */
	const hasPermissionLevel = (resource: string, level: string): boolean => {
		if (isLoading) return false;
		
		// Check for exact permission
		const exactPermission = `${level}${resource.charAt(0).toUpperCase() + resource.slice(1)}`;
		if (userPermissions.includes(exactPermission)) return true;

		// Check for manage permission (implies all other permissions)
		const managePermission = `manage${resource.charAt(0).toUpperCase() + resource.slice(1)}`;
		if (userPermissions.includes(managePermission)) return true;

		// For backward compatibility, check alternative formats
		const altPermission = `${level}_${resource}`;
		return userPermissions.includes(altPermission);
	};

	/**
	 * Check if user can view specific dashboard cards
	 * @param cardName - Dashboard card identifier
	 * @returns boolean
	 */
	const canViewDashboardCard = (cardName: string): boolean => {
		if (isLoading) return false;
		
		// CEO and similar high-level roles can view everything
		if (hasPermission('viewDashboard') && userPermissions.length > 10) return true;
		
		// Specific card permissions
		const cardPermissions: Record<string, string> = {
			'funding-sources': 'viewFundingSources',
			'withdrawal-channels': 'viewWithdrawalChannels',
			'net-liquidity': 'viewNetLiquidity',
			'escrow-balance': 'viewEscrowBalance',
			'location-balance': 'viewLocationBalance',
			'transaction-analytics': 'viewTransactionAnalytics'
		};

		return hasPermission(cardPermissions[cardName] || cardName);
	};

	/**
	 * Get user's role level based on permissions
	 * @returns string - Role level
	 */
	const getUserRoleLevel = (): 'admin' | 'manager' | 'operator' | 'viewer' => {
		if (isLoading) return 'viewer';
		
		const permissionCount = userPermissions.length;
		
		if (hasPermission('manageRoles') || hasPermission('managePermissions')) {
			return 'admin';
		} else if (permissionCount > 20) {
			return 'manager';
		} else if (permissionCount > 10) {
			return 'operator';
		} else {
			return 'viewer';
		}
	};

	/**
	 * Check if user is a super admin (has all system management permissions)
	 * @returns boolean
	 */
	const isSuperAdmin = (): boolean => {
		return hasAllPermissions(['manageRoles', 'managePermissions', 'assignPermissions']);
	};

	/**
	 * Get filtered menu items based on user permissions
	 * @param menuItems - Array of menu items with permission requirements
	 * @returns Filtered menu items
	 */
	const getFilteredMenuItems = (menuItems: any[]) => {
		if (isLoading) return [];
		
		return menuItems.filter(item => {
			if (!item.requiredPermissions) return true;
			
			if (Array.isArray(item.requiredPermissions)) {
				return hasAnyPermission(item.requiredPermissions);
			} else {
				return hasPermission(item.requiredPermissions);
			}
		});
	};

	return {
		// Core permission checking
		hasPermission,
		hasHierarchicalPermission,
		hasAnyPermission,
		hasAllPermissions,
		hasPermissionLevel,
		
		// Dashboard specific
		canViewDashboardCard,
		
		// Role/User info
		getUserRoleLevel,
		isSuperAdmin,
		
		// Utilities
		getFilteredMenuItems,
		userPermissions,
		isLoading
	};
};

export default usePermissions;
